import React, { useEffect, useState } from 'react';
import api from '../api';

// Administrácia → Financie: nastavenia odhadov modulu Financie pre firmu.
interface Bracket { rate: number; upTo: number | null }
interface Settings { vat_period: 'monthly' | 'quarterly'; income_tax_rate: 'auto' | number }

const eur0 = (n: number) => `${n.toLocaleString('sk-SK')} €`;
const pct = (r: number) => `${Math.round(r * 100)} %`;

export default function FinanceSettings() {
  const [settings, setSettings] = useState<Settings | null>(null);
  const [brackets, setBrackets] = useState<Bracket[]>([]);
  const [taxYear, setTaxYear] = useState<number | null>(null);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get('/finance/settings')
      .then(r => { setSettings(r.data.settings); setBrackets(r.data.tax_brackets || []); setTaxYear(r.data.tax_year); })
      .catch(e => setMsg({ ok: false, text: e?.response?.data?.error || 'Nastavenie sa nepodarilo načítať.' }));
  }, []);

  const save = async (patch: Partial<Settings>) => {
    setSaving(true); setMsg(null);
    try {
      const r = await api.put('/finance/settings', patch);
      setSettings(r.data.settings);
      setMsg({ ok: true, text: 'Uložené.' });
    } catch (e: any) {
      setMsg({ ok: false, text: e?.response?.data?.error || 'Uloženie zlyhalo.' });
    } finally { setSaving(false); }
  };

  // Popis pásma: "príjmy do 100 000 €", "príjmy od 100 000 € do 5 000 000 €", "príjmy nad 5 000 000 €"
  const bracketLabel = (i: number) => {
    const b = brackets[i];
    const from = i > 0 ? brackets[i - 1].upTo : null;
    if (b.upTo !== null && from === null) return `príjmy do ${eur0(b.upTo)}`;
    if (b.upTo !== null && from !== null) return `príjmy od ${eur0(from)} do ${eur0(b.upTo)}`;
    return `príjmy nad ${eur0(from || 0)}`;
  };

  const box: React.CSSProperties = { backgroundColor: 'white', padding: '1.5rem', borderRadius: 8, boxShadow: '0 1px 3px rgba(0,0,0,0.1)', maxWidth: 640 };
  const label: React.CSSProperties = { display: 'block', fontWeight: 600, color: '#374151', fontSize: '0.9rem', marginBottom: '0.35rem' };
  const select: React.CSSProperties = { width: '100%', padding: '0.55rem 0.7rem', border: '1px solid #d1d5db', borderRadius: 6, fontSize: '0.95rem', background: '#fff' };
  const hint: React.CSSProperties = { color: '#6b7280', fontSize: '0.82rem', margin: '0.35rem 0 1.25rem' };

  if (!settings) return <div style={box}>{msg ? <span style={{ color: '#b91c1c' }}>{msg.text}</span> : 'Načítavam…'}</div>;

  return (
    <div style={box}>
      <h2 style={{ margin: '0 0 0.25rem', color: '#1f2937', fontSize: '1.1rem' }}>Financie</h2>
      <p style={{ ...hint, marginTop: 0 }}>Z týchto nastavení vychádzajú odhady v module Financie.</p>

      <label style={label} htmlFor="fin-vat">Obdobie DPH</label>
      <select id="fin-vat" style={select} value={settings.vat_period} disabled={saving}
        onChange={e => save({ vat_period: e.target.value as Settings['vat_period'] })}>
        <option value="monthly">mesačné</option>
        <option value="quarterly">štvrťročné</option>
      </select>
      <p style={hint}>Podľa neho sa počíta, za ktoré obdobie a do kedy treba DPH zaplatiť.</p>

      <label style={label} htmlFor="fin-tax">Sadzba dane z príjmu{taxYear ? ` (${taxYear})` : ''}</label>
      <select id="fin-tax" style={select} value={String(settings.income_tax_rate)} disabled={saving}
        onChange={e => save({ income_tax_rate: e.target.value === 'auto' ? 'auto' : Number(e.target.value) })}>
        <option value="auto">automaticky podľa výšky príjmov</option>
        {brackets.map((b, i) => (
          <option key={b.rate} value={String(b.rate)}>{pct(b.rate)} · {bracketLabel(i)}</option>
        ))}
      </select>
      <p style={hint}>
        Pri automatickej voľbe sa sadzba určí z príjmov prepočítaných na celý rok. Pevnú sadzbu zvoľ, ak vieš,
        do ktorého pásma firma na konci roka padne. Minimálna daň sa vždy riadi príjmami.
      </p>

      {msg && <div style={{ fontSize: '0.85rem', color: msg.ok ? '#15803d' : '#b91c1c' }}>{msg.text}</div>}
    </div>
  );
}
